import { cn } from '@bazaar-marketplace/utils';
import { ChevronRightIcon } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';

import { SheetDescription, SheetHeader, SheetTitle } from '~/components';
import { partnerMarketplaceEnum } from '~/config/partner-marketplace-config';
import { openTelegramOrRegularLink } from '~/lib/open-link';
import { MintIndicator } from '../mint/mint-indicator';

interface PartnerMarketPlaceSheetProps {
  mintedNft?: boolean;
}

export function PartnerMarketplacesSheet({ mintedNft }: PartnerMarketPlaceSheetProps) {
  const t = useTranslations('my-collection.nft.actions.partner-marketplaces');
  const tActions = useTranslations('my-collection.nft.actions');

  const handleMarketplaceClick = (url: string) => {
    openTelegramOrRegularLink(url);
  };

  return (
    <>
      <SheetHeader>
        <div className="flex flex-col gap-4 pt-4">
          <SheetTitle>
            <div className="text-center text-white font-bold text-[24px]">{t('title')}</div>
          </SheetTitle>
          <SheetDescription>
            <div className="font-bold text-white/60 text-[16px] text-center">{t('description')}</div>
          </SheetDescription>
        </div>
      </SheetHeader>

      <div className="flex flex-col gap-3 mt-6 pb-6">
        {partnerMarketplaceEnum.options.map((marketplace) => {
          const isComingSoon = marketplace.comingSoon ?? false;
          const isOnChainEnabled = marketplace.onChainEnabled ?? false;
          const isDisabledBecauseOfMintedNft = !isOnChainEnabled && mintedNft;
          const disabled = isComingSoon || isDisabledBecauseOfMintedNft;

          return (
            <div key={marketplace.value} className="relative">
              <button
                className={cn(
                  'flex items-center gap-3 p-4 bg-white/10 hover:bg-white/20 rounded-2xl transition-colors border border-white/25 w-full',
                  isComingSoon ? 'cursor-not-allowed' : 'cursor-pointer',
                  isDisabledBecauseOfMintedNft ? 'opacity-60 cursor-pointer' : '',
                )}
                disabled={disabled}
                type="button"
                onClick={() => {
                  if (disabled) {
                    return;
                  }

                  if (marketplace.url) {
                    handleMarketplaceClick(marketplace.url);
                  }
                }}
              >
                <div className="flex-shrink-0">
                  <Image
                    alt={marketplace.name}
                    className="size-10 rounded-full"
                    height={40}
                    src={marketplace.logoSrc}
                    width={40}
                  />
                </div>
                <div className="flex-1 text-[18px] text-left text-white font-bold text-lg flex items-center gap-2">
                  {marketplace.name}
                  {marketplace.onChainEnabled ? <MintIndicator className="ml-auto" /> : null}
                </div>
                {isComingSoon ? null : <ChevronRightIcon className="flex-shrink-0 w-5 h-5 text-white/60" />}
              </button>
              {isComingSoon ? (
                <div className="absolute right-1.5 top-1.5 text-[#362D4B] z-20 bg-white w-14 rounded-lg h-5 text-[10px] text-center whitespace-nowrap font-bold flex items-center justify-center">
                  {tActions('soon')}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
}
